export interface BooleanScopeUniformityCounts {
	on: number;
	off: number;
	total: number;
}

export type BooleanScopeUniformityState =
	| "uniform-on"
	| "uniform-off"
	| "mixed";

export interface BooleanScopeUniformity {
	state: BooleanScopeUniformityState;
	value: boolean;
	counts: BooleanScopeUniformityCounts;
}

export interface DiscreteValueCount {
	value: string;
	count: number;
}

export type DiscreteScopeUniformityState = "uniform" | "mixed" | "empty";

export interface DiscreteScopeUniformity {
	state: DiscreteScopeUniformityState;
	value: string | null;
	valueCounts: DiscreteValueCount[];
	total: number;
	setCount: number;
}

export type TextScopeUniformityState = "uniform" | "mixed" | "empty";

export interface TextScopeUniformity {
	state: TextScopeUniformityState;
	value: string | null;
	setCount: number;
	total: number;
}

export function normalizeScopeDiscreteValue(
	value: string | null | undefined,
): string | null {
	const trimmed = value?.trim();
	return trimmed ? trimmed : null;
}

export function resolveBooleanScopeUniformity(
	values: boolean[],
): BooleanScopeUniformity {
	const total = values.length;
	if (total === 0) {
		return {
			state: "uniform-off",
			value: false,
			counts: { on: 0, off: 0, total: 0 },
		};
	}

	const on = values.filter(Boolean).length;
	const off = total - on;
	const counts = { on, off, total };

	if (on === total) {
		return { state: "uniform-on", value: true, counts };
	}
	if (off === total) {
		return { state: "uniform-off", value: false, counts };
	}
	return { state: "mixed", value: false, counts };
}

export function resolveDiscreteScopeUniformity(
	values: Array<string | null | undefined>,
	options?: {
		normalize?: (value: string | null | undefined) => string | null;
	},
): DiscreteScopeUniformity {
	const normalize = options?.normalize ?? normalizeScopeDiscreteValue;
	const normalized = values.map((value) => normalize(value));
	const total = normalized.length;
	const setCount = normalized.filter((value) => value !== null).length;

	if (total === 0) {
		return {
			state: "empty",
			value: null,
			valueCounts: [],
			total: 0,
			setCount: 0,
		};
	}

	if (setCount === 0) {
		return {
			state: "empty",
			value: null,
			valueCounts: [],
			total,
			setCount: 0,
		};
	}

	const counts = new Map<string, number>();
	for (const value of normalized) {
		if (value === null) {
			continue;
		}
		counts.set(value, (counts.get(value) ?? 0) + 1);
	}

	const valueCounts = [...counts.entries()]
		.map(([value, count]) => ({ value, count }))
		.sort(
			(left, right) =>
				right.count - left.count ||
				left.value.localeCompare(right.value),
		);

	if (valueCounts.length === 1 && setCount === total) {
		return {
			state: "uniform",
			value: valueCounts[0]?.value ?? null,
			valueCounts,
			total,
			setCount,
		};
	}

	return {
		state: "mixed",
		value: null,
		valueCounts,
		total,
		setCount,
	};
}

export function resolveTextScopeUniformity(
	values: Array<string | null | undefined>,
	options?: {
		normalize?: (value: string | null | undefined) => string | null;
	},
): TextScopeUniformity {
	const discrete = resolveDiscreteScopeUniformity(values, options);

	if (discrete.state === "empty") {
		return {
			state: "empty",
			value: null,
			setCount: discrete.setCount,
			total: discrete.total,
		};
	}

	if (discrete.state === "uniform") {
		return {
			state: "uniform",
			value: discrete.value,
			setCount: discrete.setCount,
			total: discrete.total,
		};
	}

	return {
		state: "mixed",
		value: null,
		setCount: discrete.setCount,
		total: discrete.total,
	};
}

export interface NullableBooleanScopeUniformityCounts {
	on: number;
	off: number;
	unset: number;
	total: number;
}

export type NullableBooleanScopeUniformityState =
	| "empty"
	| "uniform-on"
	| "uniform-off"
	| "mixed";

export interface NullableBooleanScopeUniformity {
	state: NullableBooleanScopeUniformityState;
	value: boolean | null;
	counts: NullableBooleanScopeUniformityCounts;
}

export function resolveNullableBooleanScopeUniformity(
	values: Array<boolean | null | undefined>,
): NullableBooleanScopeUniformity {
	const total = values.length;
	if (total === 0) {
		return {
			state: "empty",
			value: null,
			counts: { on: 0, off: 0, unset: 0, total: 0 },
		};
	}

	let on = 0;
	let off = 0;
	let unset = 0;
	for (const value of values) {
		if (value === true) {
			on += 1;
		} else if (value === false) {
			off += 1;
		} else {
			unset += 1;
		}
	}

	const counts = { on, off, unset, total };

	if (unset === total) {
		return { state: "empty", value: null, counts };
	}
	if (on === total) {
		return { state: "uniform-on", value: true, counts };
	}
	if (off === total) {
		return { state: "uniform-off", value: false, counts };
	}
	if (unset === 0 && on > 0 && off > 0) {
		return { state: "mixed", value: null, counts };
	}
	if (unset > 0 && (on > 0 || off > 0)) {
		return { state: "mixed", value: null, counts };
	}
	if (on > 0 && off === 0 && unset > 0) {
		return { state: "mixed", value: null, counts };
	}
	if (off > 0 && on === 0 && unset > 0) {
		return { state: "mixed", value: null, counts };
	}

	return { state: "mixed", value: null, counts };
}
